---
title: 'The 2 December AI Act Deadline: Marking AI Content on Systems Already Live'
description: 'Generative systems that launched before August got a four-month grace period on machine-readable marking. It ends on 2 December 2026. What that means and how to prepare.'
slug: 'ai-act-december-2026-marking-deadline'
publishedAt: '2026-11-02'
author: 'Syrcha'
tags: ['EU AI Act', 'Compliance', 'Generative AI']
readingMinutes: 7
draft: true
---

Most of Article 50 became enforceable on 2 August 2026. One obligation didn't, for one specific group.

Providers of generative AI systems **already on the market before 2 August** got until **2 December 2026** to meet the machine-readable marking requirement. That's roughly four weeks away, and it's the last AI Act date of the year aimed at ordinary companies instead of high-risk operators.

Shipped an AI feature before August? This is your deadline. Shipped after? You needed marking from day one.

## What the obligation actually is

Providers of generative AI have to mark synthetic audio, image, video and text output in a **machine-readable format**, detectable as artificially generated or manipulated.

The key word is machine-readable. This isn't the visible deepfake label. That's a separate deployer obligation, covered in our [Article 50 guide](/blog/eu-ai-act-article-50-website-requirements). This one is about embedded, technically detectable provenance: metadata, watermarking, or cryptographic signing that survives normal handling and lets downstream systems identify the content as AI-generated.

The regulation asks for solutions that are effective, interoperable, robust and reliable, as far as technically feasible, with cost and the state of the art taken into account. That phrasing matters, because it builds proportionality in. Nobody expects a small company to invent a watermarking scheme.

## Who this applies to

The obligation sits with **providers**: you develop the generative system, or have it developed, and place it on the EU market under your own name.

Build or fine-tune a model whose output reaches the public and you're in scope.

If you're only _using_ someone else's generative AI, whether calling an API or embedding a vendor's feature, the marking obligation sits with them. You still inherit the practical risk though. If your product surfaces unmarked AI content to users, that's your product doing it, which makes the vendor question below the single most useful thing on this list.

## The exemptions

The obligation doesn't apply to:

- **Short sequences** of numbers, symbols or letters
- **Source code**
- **Machine-to-machine output** no person is exposed to
- **Closed-loop industrial and product-development** contexts
- **Standard assistive editing functions** that don't substantially alter the input

That last one is what most publishers rely on, and the line is worth stating clearly. Running your own draft through a grammar or style tool is assistive editing. Generating the article isn't.

## What "content generated before August" means

Content produced before 2 August 2026 doesn't need retroactive labelling. Nobody expects you to go back and re-mark an archive.

The deadline concerns the **system**. From 2 December, a pre-existing generative system has to mark what it produces going forward.

## What to actually do in the next four weeks

Start by working out whether you're a provider at all. Most companies aren't. If every generative feature you offer is someone else's model behind an API, your obligation is largely diligence, not engineering.

Then ask your vendors, in writing, three questions. Does your output carry machine-readable marking today? In what format? Will you meet the 2 December date for systems that predate August? Get it in writing, because written answers establish what you reasonably relied on.

If you are a provider, pick a provenance approach. The industry has largely converged on content credentials and embedded provenance metadata as the practical mechanism, and an established standard is considerably more defensible than a bespoke scheme. The AI Act doesn't mandate a specific technology. It sets an outcome.

**Check that your pipeline doesn't strip the marking.** This is the failure mode that will catch people. Image processing, re-encoding, CDN optimisation and thumbnail generation routinely discard metadata. Marking applied at generation and destroyed at upload isn't marking. Test end to end, from generation to what the user actually receives.

**Write down your reasoning.** Where you rely on an exemption, or on technical infeasibility, record why. The standard is qualified by feasibility and cost, and a contemporaneous note explaining your assessment is worth a great deal more than a reconstruction afterwards.

## What this does not cover

Three things get conflated with this one constantly:

- **Chatbot disclosure**: already in force since August, and a different obligation. See our [chatbot decision guide](/blog/chatbot-ai-disclosure-requirements).
- **Visible deepfake labelling**: a deployer duty, also already in force, and not satisfied by machine-readable marking.
- **High-risk obligations**: deferred to December 2027 and August 2028, and irrelevant to most websites. We covered [what was actually delayed](/blog/ai-act-delay-what-actually-changed).

## The takeaway

Built a generative feature that went live before August? 2 December is a real date with real exposure behind it. Only use third-party AI? Your work is a vendor email and a check that your pipeline preserves what they give you.

Either way, the useful thing this month is the inventory: list every place AI generates output that reaches a person, and establish who the provider is for each one. Most of the compliance question answers itself once that list exists.

---

_This article is general information, not legal advice. Whether a particular system makes you a provider is a fact-specific question, particularly where you fine-tune or substantially modify a third-party model._

**Sources:** [European Commission: Transparency obligations under Article 50](https://digital-strategy.ec.europa.eu/en/faqs/transparency-obligations-under-article-50-ai-act) · [Article 50, EU AI Act (full text)](https://artificialintelligenceact.eu/article/50/) · [Gibson Dunn: EU AI Act Omnibus agreement](https://www.gibsondunn.com/eu-ai-act-omnibus-agreement-postponed-high-risk-deadlines-and-other-key-changes/)
